import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import logo from '../../../assets/images/logo.svg';
import logotext from '../../../assets/images/logo-text2.svg';
import './SensorAlertAcknowledgement.scss';
import { selectConfigData } from '../../../redux/config/configSlice';
import { useAppSelector } from '../../../redux/hooks';

type ViewState = 'idle' | 'acknowledging' | 'acknowledged' | 'missing-id';

const ADMIN_TOKEN = 'srx';
const ADMIN_SUFFIX = '-admin';

const getAdminBaseUrl = (baseUrl?: string | null): string | null => {
    if (!baseUrl) return null;

    const idx = baseUrl.indexOf(ADMIN_TOKEN);
    if (idx === -1) {
        console.warn('getAdminBaseUrl: "srx" token not found in baseUrl, returning original:', baseUrl);
        return baseUrl;
    }

    const insertAt = idx + ADMIN_TOKEN.length;
    return baseUrl.slice(0, insertAt) + ADMIN_SUFFIX + baseUrl.slice(insertAt);
};

const SensorAlertAcknowledgement: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const configData = useAppSelector(selectConfigData);
    const [view, setView] = useState<ViewState>(id ? 'idle' : 'missing-id');

    const isMountedRef = useRef(true);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const adminBaseUrl = getAdminBaseUrl(configData?.appApiBaseUrl);

    const handleAcknowledge = useCallback(async () => {
        if (!id) {
            setView('missing-id');
            return;
        }
        if (!adminBaseUrl) {
            toast.error('Configuration is still loading. Please try again in a moment.');
            return;
        }
        if (view === 'acknowledging' || view === 'acknowledged') return;

        setView('acknowledging');

        try {
            const res = await fetch(`${adminBaseUrl}/sensor/acknowledgealert?id=${id}`, {
                method: 'PATCH',
            });

            if (!isMountedRef.current) return;

            const data = await res.json();

            if (res.ok && data?.success) {
                toast.success(data?.message || 'Alert acknowledged.');
                setView('acknowledged');
            } else {
                toast.error(data?.message || 'Failed to acknowledge alert.');
                setView('idle');
            }
        } catch (err) {
            if (!isMountedRef.current) return;
            console.error('Error acknowledging alert:', err);
            setView('idle');
        }
    }, [id, adminBaseUrl, view]);

    const renderIcon = (state: ViewState) => {
        if (state === 'acknowledged') {
            return (
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#2FA84F" strokeWidth="2" />
                    <path
                        d="M7.5 12.5L10.3 15.3L16.5 9"
                        stroke="#2FA84F"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            );
        }

        return (
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
                    stroke="#D34343"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <line x1="12" y1="9" x2="12" y2="13" stroke="#D34343" strokeWidth="2" strokeLinecap="round" />
                <line x1="12" y1="17" x2="12.01" y2="17" stroke="#D34343" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
        );
    };

    const renderMessage = () => {
        if (view === 'missing-id') {
            return (
                <p className="ack-card__message ack-card__message--error">
                    We couldn&apos;t find a sensor to acknowledge. Please use the link from your original alert
                    notification.
                </p>
            );
        }
        if (view === 'acknowledged') {
            return <p className="ack-card__message">Thanks — alert acknowledged successfully.</p>;
        }
        return (
            <p className="ack-card__message">
                We have received an alert for sensor.
                <br />
                Please acknowledge the alert.
            </p>
        );
    };

    const isButtonDisabled = view === 'acknowledging' || view === 'acknowledged' || view === 'missing-id';

    const renderButtonContent = () => {
        switch (view) {
            case 'acknowledging':
                return (
                    <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Acknowledging...
                    </>
                );
            case 'acknowledged':
                return (
                    <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="12" r="10" fill="#ffffff" fillOpacity="0.2" />
                            <polyline
                                points="8 12.5 10.5 15 16 9"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        Acknowledged
                    </>
                );
            default:
                return (
                    <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <polyline
                                points="20 6 9 17 4 12"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        Acknowledge
                    </>
                );
        }
    };

    return (
        <div className="ack-page-wrapper">
            <div className="ack-bg-blob ack-bg-blob--1" />
            <div className="ack-bg-blob ack-bg-blob--2" />

            <div className="ack-card">
                <div className="ack-card__header">
                    <div className="ack-card__header__logo">
                        <img src={logo} alt="home" style={{ width: '40px' }} />
                        <span className="ack-card__header__logo__text">
                            <ReactSVG className="svg-box" wrapper="span" src={logotext} />
                        </span>
                    </div>
                </div>

                <div className={`ack-icon-ring${view === 'acknowledged' ? ' ack-icon-ring--done' : ''}`}>
                    <div className="ack-icon-ring__pulse" />
                    <div className="ack-icon-ring__inner">{renderIcon(view)}</div>
                </div>

                <div className="ack-card__body">
                    <h1 className="ack-card__title">Sensor Alert Acknowledgement</h1>
                    <div aria-live="polite">{renderMessage()}</div>
                </div>

                <div className="ack-actions">
                    <Button
                        id="btn-acknowledge-alert"
                        className={`ack-btn ack-btn--acknowledge${view === 'acknowledged' ? ' ack-btn--done' : ''}`}
                        onClick={handleAcknowledge}
                        disabled={isButtonDisabled}
                        aria-disabled={isButtonDisabled}
                        aria-busy={view === 'acknowledging'}
                    >
                        {renderButtonContent()}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default SensorAlertAcknowledgement;